'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createABTestService,
  deleteABTestService,
  updateABTestService,
} from '@/services/httpServices/ABTestServices';
import type {
  ABTestCreateResponse,
  ABTestUpdateResponse,
  CreateABTestServiceConfig,
  DeleteABTestServiceConfig,
  UpdateABTestServiceConfig,
} from '@/services/httpServices/ABTestServices/types';

import { abTestByIdKey, abTestListKeys } from '../queries/abTestQueryKeys';

export function useCreateABTestMutation() {
  const queryClient = useQueryClient();

  return useMutation<ABTestCreateResponse, Error, CreateABTestServiceConfig>({
    mutationFn: async (config: CreateABTestServiceConfig) => {
      return createABTestService(config);
    },
    onSuccess: async () => {
      // Invalidate AB test list queries to show the new test
      await queryClient.invalidateQueries({
        queryKey: abTestListKeys(),
      });
    },
  });
}

export function useUpdateABTestMutation() {
  const queryClient = useQueryClient();

  return useMutation<ABTestUpdateResponse, Error, UpdateABTestServiceConfig>({
    mutationFn: async (config: UpdateABTestServiceConfig) => {
      return updateABTestService(config);
    },
    onSuccess: async (_, variables) => {
      // Invalidate AB test list queries
      await queryClient.invalidateQueries({
        queryKey: abTestListKeys(),
      });

      // Invalidate the specific AB test by ID query
      // Extract id from nested params structure
      const abTestId = variables.params?.params?.id ?? '';
      if (abTestId !== '') {
        await queryClient.invalidateQueries({
          queryKey: abTestByIdKey({
            httpRequestConfig: variables.httpRequestConfig,
            params: { id: abTestId },
          }),
        });
      }
    },
  });
}

export function useDeleteABTestMutation() {
  const queryClient = useQueryClient();

  return useMutation<undefined, Error, DeleteABTestServiceConfig>({
    mutationFn: async (config: DeleteABTestServiceConfig) => {
      await deleteABTestService(config);
      return undefined;
    },
    onSuccess: async () => {
      // Invalidate AB test list queries
      await queryClient.invalidateQueries({
        queryKey: abTestListKeys(),
      });
    },
  });
}
